import * as React from "react";
import { formatDistance } from "date-fns";
import { isBrowser, isMobile } from "react-device-detect";
import { cx } from "utils/join-classnames";
import {
    Icon,
    Button,
    Spinner,
    Tooltip,
    EditModal,
    VideoPlayer,
    InfoModal,
    PublishModal,
    OptionsPopover,
    ConfirmActionModal,
    FlagBar,
    MetadataModal,
    ShareFacebookModal,
    ShareTwitterModal,
    ShareYoutubeModal,
    SubtitlesModal,
} from "ui/components";
import {
    Option,
    EditOp,
    Fragment,
    MediaFile,
    SubtitleExportValue,
    YoutubePublisherParams,
    TwitterPublisherParams,
    FacebookPublisherParams,
    PublishOp,
    VideoActionRequest,
} from "core/models";
import formatDuration from "utils/formatDuration";
import formatByteCount from "utils/formatByteCount";
import { useSessionContext } from "ui/context/SessionContext";
import { usePublishContext } from "ui/context/PublishContext";
import { useNotifications } from "ui/context/NotificationsContext";
import {
    VariantSetting,
    listMediaFiles,
    EditRequest,
    updateFragment as updateFragmentAPI,
    UpdateFragment,
    updateThumbnail,
} from "core/services/";
import usePermissions from "ui/hooks/usePermissions";
import usePublish from "ui/hooks/usePublish";
import * as fragmentEditController from "core/controllers/fragmentEdits";
import styles from "./FragmentCard.module.scss";
import pwa_styles from "./FragmentCard_Pwa.module.scss";

const instanceOfEdit = (data: any): data is EditOp => "cropped" in data;
const isAddon = (props: EditOp) =>
    props.introkey || props.outrokey || props.overlaykey;

const shortUsername = (username: string): string => {
    if (username.length < 15) {
        return username;
    }
    if (username.search("@") === -1) {
        return `${username.substr(0, 15)}…`;
    }
    return username.split("@")[0];
};

interface Props {
    fragment: Fragment;
    selectable: boolean;
    selectedNumber: number;
    onSelect(fragmentID: number): void;
    onDelete(fragmentID: number): void;
    onEdit(
        fragment: Fragment,
        notificationId: string,
        successText: string,
    ): void;
    onUpdate(): void;
}

const FragmentCard: React.FC<Props> = ({
    fragment,
    selectable,
    selectedNumber,
    onSelect,
    onDelete,
    onEdit,
    onUpdate,
}: Props) => {
    const userPermissions = usePermissions();
    const { share, publish } = usePublish();
    const { addNotification, updateNotification } = useNotifications();
    const [showVideo, setShowVideo] = React.useState<boolean>(false);
    const [showSubtitlesModal, setSubtitlesModal] =
        React.useState<boolean>(false);
    const [showPublishModal, setPublishModal] = React.useState<boolean>(false);
    const [showYoutubeModal, setYoutubeModal] = React.useState<boolean>(false);
    const [showTwitterModal, setTwitterModal] = React.useState<boolean>(false);
    const [showFacebookModal, setFacebookModal] =
        React.useState<boolean>(false);
    const [showDownloadModal, setDownloadModal] =
        React.useState<boolean>(false);
    const [showEditModal, setEditModal] = React.useState<boolean>(false);
    const [showMetadataModal, setMetadataModal] =
        React.useState<boolean>(false);
    const [confirmDelete, setConfirmDelete] = React.useState<boolean>(false);
    const [publishFormats, setPublishFormats] = React.useState<
        VariantSetting[]
    >([]);
    const [addons, setAddons] = React.useState<MediaFile[]>([]);
    const [showInfoModal, setShowInfoModal] = React.useState<boolean>(false);
    const [videoPlayerRequest, setVideoPlayerRequest] =
        React.useState<VideoActionRequest>({ type: "none" });
    const sessionContext = useSessionContext();
    const publishContext = usePublishContext();

    const checkShared = () => {
        const sharedOnSocial = {
            youtube: false,
            twitter: false,
            facebook: false,
        };

        fragment.properties.forEach((value) => {
            if (value.key === "spoton-publishops") {
                const publishOps: PublishOp[] = value.value as PublishOp[];
                publishOps.forEach((publishOp) => {
                    switch (publishOp.publisherkey) {
                        case "default_youtube":
                            sharedOnSocial.youtube = true;
                            break;
                        case "default_twitter":
                            sharedOnSocial.twitter = true;
                            break;
                        case "default_facebook":
                            sharedOnSocial.facebook = true;
                            break;
                        default:
                            break;
                    }
                });
            }
        });
        return sharedOnSocial;
    };

    const checkPublished = () => {
        const publishedCheck = {
            published: false,
        };

        fragment.properties.forEach((value) => {
            if (value.key === "spoton-publishops") {
                const publishOps: PublishOp[] = value.value as PublishOp[];
                publishOps.forEach((publishOp) => {
                    if (publishOp.publishertype === "Publish") {
                        publishedCheck.published = true;
                    }
                });
            }
        });
        return publishedCheck;
    };

    const [sharedOnSocial, setSharedOnSocial] = React.useState(checkShared());
    const [publishedCheck, setPublishedCheck] =
        React.useState(checkPublished());

    const userCanPublish = userPermissions.canPublish;
    const userCanShare = userPermissions.canShare;

    React.useEffect(() => {
        if (selectable) {
            setShowVideo(false);
        }
    }, [selectable]);

    React.useEffect(() => {
        // Autoplay workaround, because setting autoplay attr on element causes errors 🤷🏻‍♂️ Sorry
        setVideoPlayerRequest({ type: "play" });
    }, [showVideo]);

    const hasAddon =
        fragment.properties.filter(
            (prop) =>
                prop.key === "spoton-editop" &&
                instanceOfEdit(prop.value) &&
                isAddon(prop.value),
        ).length > 0;

    React.useMemo(() => {
        const existingFormats: VariantSetting[] = fragment.variants.map(
            (variant) => ({
                name: variant.formatName,
                formatkey: variant.formatKey,
                tags: variant.tags,
            }),
        );
        if (userPermissions.canCreateVariants) {
            let allPublishFormats = existingFormats.concat(
                publishContext.publishFormats,
            );
            allPublishFormats = allPublishFormats.filter(
                (thing, index, self) =>
                    index ===
                    self.findIndex((t) => t.formatkey === thing.formatkey),
            );
            setPublishFormats(allPublishFormats);
        } else {
            setPublishFormats(existingFormats);
        }
    }, [publishContext.publishFormats, fragment.variants]);

    const handleConfirmDelete = (shouldDelete: boolean) => {
        setConfirmDelete(false);
        if (shouldDelete) {
            onDelete(fragment.id);
        }
    };

    const handleOpenEditModal = async () => {
        try {
            if (!sessionContext.session) {
                throw Error("missing session");
            }
            if (!sessionContext.selectedProject) {
                throw Error("missing selected project");
            }
            setAddons([]);
            setEditModal(true);

            const availableAddons = await listMediaFiles(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
            );
            if (availableAddons.error) {
                throw Error(availableAddons.error);
            }
            setAddons(availableAddons.value || []);
        } catch (err) {
            addNotification(
                "error",
                "Unable to get addons",
                (err as Error).message,
                5000,
            );
            setEditModal(false);
        }
    };

    const handleOpenPublishModal = React.useCallback(() => {
        setPublishModal(true);
    }, []);
    const handleClosePublishModal = () => {
        setPublishModal(false);
    };

    const handleSavePublishModal = (
        publishOption: string,
        publishModule: string,
    ) => {
        setPublishModal(false);
        publish(fragment, publishOption, publishModule);
        setPublishedCheck({ ...publishedCheck, published: true });
    };
    const handleDownload = (selectedOutput: string) => {
        if (!sessionContext.session) {
            return;
        }
        setDownloadModal(false);
        fragmentEditController.handleDownload(
            selectedOutput,
            fragment,
            sessionContext.session.token,
            addNotification,
            updateNotification,
        );
    };

    const handleOpenSubtitlesModal = (): void => setSubtitlesModal(true);
    const handleCloseSubtitlesModal = (): void => setSubtitlesModal(false);
    const handleSaveSubtitlesModal = (
        editedFragment: Fragment,
        notificationId: string,
    ): void => {
        onEdit(editedFragment, notificationId, "Subtitles processed");
        setSubtitlesModal(false);
    };
    const handleCloseYoutubeModal = () => {
        setYoutubeModal(false);
    };

    const handleOpenDownloadModal = React.useCallback(
        () => setDownloadModal(true),
        [],
    );

    const handleCloseTwitterModal = () => {
        setTwitterModal(false);
    };
    const handleCloseFacebookModal = () => {
        setFacebookModal(false);
    };
    const handleCloseDownloadModal = () => {
        setDownloadModal(false);
    };

    const handleSaveEditModal = (editRequest: EditRequest) => {
        if (!sessionContext.session) {
            return;
        }
        setEditModal(false);
        fragmentEditController.handleEdit(
            editRequest,
            fragment,
            sessionContext.session.token,
            addNotification,
            updateNotification,
            onEdit,
        );
    };
    const handleCloseEditModal = () => {
        setEditModal(false);
    };

    const handleSaveYoutubeModal = async (
        selectedAccount: string,
        categoryid: number,
        embeddable: boolean,
        privacystatus: string,
        title: string,
        description: string,
        tags: string[],
        subtitleExport: SubtitleExportValue,
    ) => {
        setYoutubeModal(false);

        const publisherParams: YoutubePublisherParams = {
            categoryid,
            embeddable,
            privacystatus,
            title,
            description,
            tags,
            usefragmenttitleastitle: false,
            usefragmenttitleasdescription: false,
        };
        const [shareStatus, sharedFragment, shareNotificationId] = await share(
            "YouTube",
            fragment,
            selectedAccount,
            subtitleExport,
            publisherParams,
        );
        if (!shareStatus || !sharedFragment) {
            return;
        }
        onEdit(sharedFragment, shareNotificationId, "YouTube publish done");
        setSharedOnSocial({ ...sharedOnSocial, youtube: true });
    };
    const handleSaveTwitterModal = async (
        selectedAccount: string,
        tweet: string,
        subtitleExport: SubtitleExportValue,
    ) => {
        setTwitterModal(false);
        const publisherParams: TwitterPublisherParams = {
            usefragmenttitleastweet: false,
            tweet,
        };
        const [shareStatus, sharedFragment, shareNotificationId] = await share(
            "Twitter",
            fragment,
            selectedAccount,
            subtitleExport,
            publisherParams,
        );
        if (!shareStatus || !sharedFragment) {
            return;
        }
        onEdit(sharedFragment, shareNotificationId, "Twitter publish done");
        setSharedOnSocial({ ...sharedOnSocial, twitter: true });
    };
    const handleSaveFacebookModal = async (
        selectedAccount: string,
        selectedPage: string,
        videoTitle: string,
        desc: string,
        status: string,
        embedding: boolean,
        category: string,
        subtitleExport: SubtitleExportValue,
    ) => {
        setFacebookModal(false);
        const publisherParams: FacebookPublisherParams = {
            pageid: selectedPage,
            title: videoTitle,
            description: desc,
            unpublishedcontenttype: status,
            embeddable: embedding,
            category,
            usefragmenttitleastitle: false,
            usefragmenttitleasdescription: false,
        };
        const [shareStatus, sharedFragment, shareNotificationId] = await share(
            "Facebook",
            fragment,
            selectedAccount,
            subtitleExport,
            publisherParams,
        );
        if (!shareStatus || !sharedFragment) {
            return;
        }
        onEdit(sharedFragment, shareNotificationId, "Facebook publish done");
        setSharedOnSocial({ ...sharedOnSocial, facebook: true });
    };

    const handleMetadataSave = async (changedProps: UpdateFragment) => {
        if (!sessionContext.session) {
            return;
        }
        setMetadataModal(false);
        const result = await updateFragmentAPI(
            sessionContext.session.token,
            changedProps,
        );
        if (result.error || !result.value) {
            addNotification(
                "error",
                "error",
                "Something went wrong updating the clip information, try again",
                0,
            );
            return;
        }
        addNotification(
            "success",
            "Clip information was updated",
            `For ${fragment.title}`,
            5000,
        );
        onUpdate();
    };
    const handleNewThumbnail = async (thumbnailId: number) => {
        try {
            if (!sessionContext.session) {
                throw Error("no session");
            }
            const updateResult = await updateThumbnail(
                sessionContext.session.token,
                {
                    id: fragment.id,
                    thumbnailFileId: thumbnailId,
                    ExcludeOtherFieldsIfProcessing: false,
                },
            );
            if (updateResult.error || !updateResult.value) {
                throw new Error("Update Thumbnail failed");
            }
            const thumbNotifications = addNotification(
                "normal",
                `Updating thumbnail for ${fragment.title}`,
                "Hold on",
                0,
            );
            onEdit(
                updateResult.value,
                thumbNotifications.id,
                `For ${fragment.title}`,
            );
        } catch (err) {
            addNotification("error", `Error`, (err as Error).message, 0);
        }
    };

    const getThumbnailURL = React.useCallback(
        (targetFragment: Fragment) => {
            if (targetFragment.getMediaType() === "Image" && showVideo) {
                return targetFragment.getMediaURL();
            }
            return targetFragment.thumbnail
                ? targetFragment.getThumbnailURL()
                : undefined;
        },
        [showVideo],
    );
    const thumbnailURL = getThumbnailURL(fragment);
    const thumbnailStyle: React.CSSProperties = {
        backgroundImage:
            fragment.thumbnail && !showVideo
                ? `url("${thumbnailURL}")`
                : undefined,
    };

    const userCanEdit = userPermissions.canEditFragment;

    const editOptions: Option[] = React.useMemo(
        () => [
            {
                title: "Edit clip",
                icon: "edit",
                onClick: handleOpenEditModal,
                disabled: !userCanEdit,
                tooltip: !userCanEdit
                    ? "Editing clips is not available in your current plan"
                    : undefined,
            },
            {
                title: "Captions",
                icon: "subtitles",
                onClick: handleOpenSubtitlesModal,
                tooltip: !userPermissions.canCreateCaptions
                    ? "Captions is not available within your plan"
                    : undefined,
                disabled: !userPermissions.canCreateCaptions,
            },
            {
                title: "Change Metadata",
                icon: "settings",
                onClick: () => setMetadataModal(true),
            },
        ],
        [sessionContext.userRoles],
    );

    const options: Option[] = React.useMemo(
        () => [
            {
                title: "Delete",
                icon: "delete",
                onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                    setConfirmDelete(true);
                },
                disabled: !userPermissions.canDeleteFragment,
            },
            {
                title: "Info",
                icon: "info",
                onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                    setShowInfoModal(true);
                },
            },
        ],
        [sessionContext.userRoles, sessionContext.selectedProject?.key],
    );

    const pwaOptions: Option[] = React.useMemo(
        () => [
            {
                title: "Delete",
                icon: "delete",
                onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                    setConfirmDelete(true);
                },
                disabled: !userPermissions.canDeleteFragment,
            },
        ],
        [sessionContext.userRoles, sessionContext.selectedProject?.key],
    );

    const shareOptions: Option[] = React.useMemo(
        () => [
            {
                title: "Youtube",
                icon: "youtube",
                onClick: () => setYoutubeModal(true),
                disabled:
                    publishContext.activeYoutubeAccounts.length <= 0 ||
                    !userCanShare,
                shared: sharedOnSocial.youtube,
            },
            {
                title: "Twitter",
                icon: "twitter",
                onClick: () => setTwitterModal(true),
                disabled:
                    publishContext.activeTwitterAccounts.length <= 0 ||
                    !userCanShare,
                shared: sharedOnSocial.twitter,
            },
            {
                title: "Facebook",
                icon: "facebook",
                onClick: () => setFacebookModal(true),
                disabled:
                    publishContext.activeFacebookAccounts.length <= 0 ||
                    !userCanShare,
                shared: sharedOnSocial.facebook,
            },
        ],
        [publishContext, sharedOnSocial],
    );

    // Leave this part commented until Publishing becomes an official feature

    // const pwaShareOptions: Option[] = React.useMemo(
    //     () => [
    //         {
    //             title: "Share",
    //             icon: "share",
    //             onClick: () => {
    //                 shareVideo();
    //             },
    //             disabled: !userCanShare,
    //         },
    //         {
    //             title: "Publish",
    //             icon: "publish",
    //             onClick: () => {
    //                 handleOpenPublishModal();
    //             },
    //             disabled: !userCanPublish,
    //         },
    //     ],
    //     [publishContext]
    // );

    const shareVideo = async () => {
        const downloadURL = fragment.getDownloadURL();
        const response = await fetch(downloadURL);
        const blob = await response.blob();
        const filesArray = [
            new File([blob], "video.mp4", { type: "video/mp4" }),
        ];
        const shareData = { files: filesArray };
        navigator.share(shareData);
    };

    return (
        <div
            className={cx(
                styles.item,
                !fragment.isAvailable() ? styles.is__processing : null,
                styles.container__item__selectable,
            )}
        >
            <div
                className={cx(
                    styles.item__selectable,
                    selectable ? styles.item__selectable__enabled : null,
                    selectedNumber > 0
                        ? styles.item__selectable__selected
                        : null,
                )}
                onClick={() => {
                    if (fragment.isAvailable() && !hasAddon) {
                        onSelect(fragment.id);
                    }
                }}
                role="presentation"
            >
                {fragment.isAvailable() && !hasAddon && (
                    <div
                        className={cx(
                            styles.item__selectable__index__count,
                            selectedNumber > 0
                                ? styles.item__selectable__index__count__selected
                                : null,
                        )}
                    >
                        {selectedNumber > 0 ? selectedNumber : ""}
                    </div>
                )}
            </div>
            <div
                className={cx(styles.item__thumbnail, styles.media)}
                style={thumbnailStyle}
                role="presentation"
            >
                <OptionsPopover
                    icon="more"
                    options={isBrowser ? options : pwaOptions}
                    disabled={selectable}
                    className={cx(
                        styles.media__morebtn,
                        selectable ? styles.hideifselectable : null,
                    )}
                />
                {(() => {
                    if (
                        fragment.isAvailable() &&
                        fragment.getMediaType() === "Image" &&
                        showVideo
                    ) {
                        return null;
                    }

                    // show a video element after the user clicks the play button
                    if (showVideo) {
                        const subtitleVariantIdx =
                            fragment.metaVariants.findIndex(
                                (variant) =>
                                    variant.formatKey === "subtitles_webvtt",
                            );
                        return (
                            <div className={styles.media__video}>
                                <VideoPlayer
                                    type="video"
                                    videoSrc={fragment.getMediaURL()}
                                    actionRequest={videoPlayerRequest}
                                    autoplay
                                    subtitleTrackSrc={
                                        subtitleVariantIdx !== -1
                                            ? fragment.getMetaVariantURL(
                                                  subtitleVariantIdx,
                                              )
                                            : undefined
                                    }
                                />
                            </div>
                        );
                    }

                    if (
                        !fragment.isAvailable() &&
                        fragment.isProcessing === false
                    ) {
                        return (
                            <div className={styles.is__processing__placeholder}>
                                <div
                                    className={cx(
                                        styles.is__processing__placeholder__failed,
                                    )}
                                >
                                    <Icon
                                        className={
                                            styles.is__processing__placeholder__failed__icon
                                        }
                                        name="error"
                                    />
                                    <div
                                        className={
                                            styles.is__processing__placeholder__label
                                        }
                                    >
                                        Processing failed
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    // return nothing if the video is being processed
                    if (!fragment.isAvailable() || fragment.isProcessing) {
                        return (
                            <div className={styles.is__processing__placeholder}>
                                <div
                                    className={
                                        styles.is__processing__placeholder__indicator
                                    }
                                >
                                    <Spinner />
                                </div>
                            </div>
                        );
                    }

                    if (selectable) {
                        return (
                            <Tooltip
                                text="Duration"
                                className={styles.media__duration__container}
                            >
                                <div className={styles.media__duration}>
                                    {fragment.variants[0] &&
                                        formatDuration(
                                            fragment.variants[0]
                                                .mediaFileProperties
                                                .mediaProperties.duration || 0,
                                        )}
                                </div>
                            </Tooltip>
                        );
                    }

                    // show a playbutton
                    return (
                        <>
                            <OptionsPopover
                                icon="more"
                                options={isBrowser ? options : pwaOptions}
                                disabled={selectable}
                                className={cx(
                                    styles.media__morebtn,
                                    selectable ? styles.hideifselectable : null,
                                )}
                            />
                            <Tooltip
                                text="Duration"
                                className={styles.media__duration__container}
                            >
                                <div className={styles.media__duration}>
                                    {formatDuration(fragment.getDuration())}
                                </div>
                            </Tooltip>
                            <div
                                className={styles.media__playbutton}
                                role="presentation"
                                onClick={() => {
                                    if (
                                        fragment.isAvailable() &&
                                        fragment.isProcessing === false &&
                                        !selectable
                                    ) {
                                        setShowVideo(true);
                                    }
                                }}
                            >
                                <Icon name="play" />
                            </div>
                        </>
                    );
                })()}
            </div>
            <div
                className={cx(
                    styles.item__content,
                    !fragment.isAvailable()
                        ? styles.item__content__unavailable
                        : null,
                )}
            >
                {fragment.isAvailable() && fragment.variants.length > 0 && (
                    <span className={styles.item__file__size}>
                        <Tooltip text="Size">
                            {formatByteCount(
                                fragment.variants[0].mediaFileProperties
                                    .mediaProperties.size,
                            )}
                        </Tooltip>
                    </span>
                )}
                {fragment.isAvailable() && <FlagBar fragment={fragment} />}
                <h1 className={styles.item__title} title={fragment.title}>
                    {fragment.title}
                </h1>
                {isBrowser && (
                    <p className={styles.item__username}>
                        {`${formatDistance(
                            new Date(),
                            new Date(`${fragment.creationDate}Z`),
                        )} ago - by ${shortUsername(
                            fragment.userProperties.username,
                        )}`}
                    </p>
                )}

                {isBrowser && (
                    <div className={styles.item__footer}>
                        {fragment.isAvailable() && (
                            <>
                                <div className={styles.item__footer__item}>
                                    <Button
                                        type="function"
                                        theme="light"
                                        icon="download"
                                        tooltip="Download"
                                        onClick={handleOpenDownloadModal}
                                        disabled={
                                            selectable ||
                                            publishContext.publishFormats
                                                .length === 0
                                        }
                                        className={
                                            selectable
                                                ? styles.hideifselectable
                                                : undefined
                                        }
                                    />
                                </div>
                                <div className={cx(styles.options__icon)}>
                                    <div className={styles.publish__icon}>
                                        <Button
                                            type="function"
                                            theme="light"
                                            onClick={handleOpenPublishModal}
                                            icon="publish"
                                            disabled={
                                                selectable ||
                                                !userCanPublish ||
                                                publishContext.publishOptions
                                                    .length === 0
                                            }
                                            className={cx(
                                                selectable
                                                    ? styles.hideifselectable
                                                    : undefined,
                                            )}
                                            tooltip={(() => {
                                                if (
                                                    !userPermissions.hasFeaturePublish
                                                ) {
                                                    return "Publishing is not available within your current plan";
                                                }
                                                if (
                                                    !userPermissions.hasRightToPublish
                                                ) {
                                                    return "Publish is not allowed";
                                                }
                                                if (
                                                    publishContext
                                                        .publishOptions
                                                        .length === 0
                                                ) {
                                                    return "No publishing options have been configured";
                                                }
                                                return "Publish";
                                            })()}
                                        />
                                    </div>
                                    {publishedCheck.published && (
                                        <div
                                            className={
                                                styles.publish__icon_shared
                                            }
                                        >
                                            <Tooltip text="Published">
                                                <Icon
                                                    className={
                                                        styles.published__icon
                                                    }
                                                    name="check-circle"
                                                />
                                            </Tooltip>
                                        </div>
                                    )}
                                </div>
                                <div className={styles.item__footer__item}>
                                    <OptionsPopover
                                        icon="share"
                                        orientation="left"
                                        options={shareOptions}
                                        disabled={
                                            selectable ||
                                            shareOptions.length <= 0 ||
                                            !userCanShare
                                        }
                                        className={
                                            selectable
                                                ? styles.hideifselectable
                                                : undefined
                                        }
                                        tooltip={
                                            !userPermissions.hasFeatureShare
                                                ? "Sharing is not available in your current plan"
                                                : undefined
                                        }
                                    />
                                </div>
                                <div className={styles.item__footer__spacer} />
                                <div className={styles.item__footer__item}>
                                    <OptionsPopover
                                        icon="edit"
                                        options={editOptions}
                                        disabled={selectable}
                                        className={
                                            selectable
                                                ? styles.hideifselectable
                                                : undefined
                                        }
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}
                {isMobile && (
                    <p className={pwa_styles.item__username}>
                        {shortUsername(fragment.userProperties.username)} -{" "}
                        {`${formatDistance(
                            new Date(),
                            new Date(`${fragment.creationDate}Z`),
                        )} ago`}
                    </p>
                )}
                {isMobile && (
                    <div className={pwa_styles.item__footer}>
                        {fragment.isAvailable() && (
                            <div className={pwa_styles.item__footer__itemrow}>
                                <div
                                    className={
                                        pwa_styles.item__footer__itemrow__item
                                    }
                                >
                                    <Button
                                        type="function"
                                        theme="light"
                                        icon="download"
                                        onClick={handleOpenDownloadModal}
                                        disabled={
                                            selectable ||
                                            publishContext.publishFormats
                                                .length === 0
                                        }
                                        className={
                                            selectable
                                                ? styles.hideifselectable
                                                : pwa_styles.item__footer__itemrow__item__btn
                                        }
                                    />
                                </div>
                                <div
                                    className={
                                        pwa_styles.item__footer__itemrow__item
                                    }
                                    // Leave this part commented until Publishing becomes an official feature
                                >
                                    {/* <OptionsPopover
                                        options={pwaShareOptions}
                                        icon={"publish"}
                                        className={
                                            pwa_styles.item__footer__itemrow__item__btn
                                        }
                                        tooltip={(() => {
                                            if (
                                                !userPermissions.hasFeaturePublish
                                            ) {
                                                return "Publishing is not available within your current plan";
                                            }
                                            if (
                                                !userPermissions.hasRightToPublish
                                            ) {
                                                return "Publish is not allowed";
                                            }
                                            if (
                                                publishContext.publishOptions
                                                    .length === 0
                                            ) {
                                                return "No publishing options have been configured";
                                            }
                                            return "Publish";
                                        })()}
                                    /> */}
                                    <Button
                                        type="function"
                                        theme="light"
                                        onClick={shareVideo}
                                        icon="share"
                                        disabled={
                                            selectable ||
                                            !userCanPublish ||
                                            publishContext.publishOptions
                                                .length === 0
                                        }
                                        className={cx(
                                            selectable
                                                ? styles.hideifselectable
                                                : pwa_styles.item__footer__itemrow__item__btn,
                                        )}
                                        tooltip={(() => {
                                            if (
                                                !userPermissions.hasFeaturePublish
                                            ) {
                                                return "Publishing is not available within your current plan";
                                            }
                                            if (
                                                !userPermissions.hasRightToPublish
                                            ) {
                                                return "Publish is not allowed";
                                            }
                                            if (
                                                publishContext.publishOptions
                                                    .length === 0
                                            ) {
                                                return "No publishing options have been configured";
                                            }
                                            return "Publish";
                                        })()}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {confirmDelete && (
                    <ConfirmActionModal
                        theme="dark"
                        title={`Really delete ${fragment.title}?`}
                        description="There is no way back from this."
                        confirmBtnText="Delete"
                        handleCancel={() => setConfirmDelete(false)}
                        handleConfirm={() => {
                            handleConfirmDelete(true);
                            setConfirmDelete(false);
                        }}
                    />
                )}
                {showPublishModal && (
                    <PublishModal
                        fragment={fragment}
                        handlePublish={handleSavePublishModal}
                        handleClose={handleClosePublishModal}
                        publishFormats={publishFormats}
                        download={false}
                    />
                )}
                {showDownloadModal && (
                    <PublishModal
                        fragment={fragment}
                        download
                        handlePublish={handleDownload}
                        handleClose={handleCloseDownloadModal}
                        publishFormats={publishFormats}
                    />
                )}
                {showYoutubeModal && (
                    <ShareYoutubeModal
                        fragment={fragment}
                        handleClose={handleCloseYoutubeModal}
                        handleShare={handleSaveYoutubeModal}
                    />
                )}
                {showTwitterModal && (
                    <ShareTwitterModal
                        fragment={fragment}
                        handleClose={handleCloseTwitterModal}
                        handleShare={handleSaveTwitterModal}
                    />
                )}
                {showFacebookModal && (
                    <ShareFacebookModal
                        fragment={fragment}
                        handleClose={handleCloseFacebookModal}
                        handleShare={handleSaveFacebookModal}
                    />
                )}
                {showEditModal && (
                    <EditModal
                        fragment={fragment}
                        handleSave={handleSaveEditModal}
                        handleClose={handleCloseEditModal}
                        addons={addons}
                    />
                )}
                {showSubtitlesModal && (
                    <SubtitlesModal
                        fragment={fragment}
                        onSave={handleSaveSubtitlesModal}
                        onClose={handleCloseSubtitlesModal}
                    />
                )}
                {showInfoModal && (
                    <InfoModal
                        fragment={fragment}
                        handleClose={() => setShowInfoModal(false)}
                    />
                )}
                {showMetadataModal && (
                    <MetadataModal
                        handleClose={() => setMetadataModal(false)}
                        onSave={handleMetadataSave}
                        fragment={fragment}
                        handleNewThumbnail={handleNewThumbnail}
                    />
                )}
            </div>
        </div>
    );
};

export default React.memo(FragmentCard);
